import type { ContactListQuery } from "@multi-chat/shared";

export type ContactsFilterState = {
  hasEmail?: "ANY" | "HAS" | "MISSING";
  hasPhone?: "ANY" | "HAS" | "MISSING";
  platform?: "ANY" | "WHATSAPP" | "FACEBOOK" | "INSTAGRAM";
  /** Local date input value (YYYY-MM-DD). */
  createdFrom?: string;
  createdTo?: string;
};

/** Maps UI filter state to the server-backed contacts list query. */
export function buildContactsListQuery(input: {
  query: ContactListQuery;
  committedSearch?: string;
  selectedGroupId: number | null;
  filters: ContactsFilterState;
}): ContactListQuery {
  const { query, committedSearch, selectedGroupId, filters } = input;
  return {
    ...query,
    search: committedSearch,
    groupId: selectedGroupId ?? undefined,
    hasEmail: filters.hasEmail === "ANY" ? undefined : filters.hasEmail,
    hasPhone: filters.hasPhone === "ANY" ? undefined : filters.hasPhone,
    platform:
      filters.platform === "ANY" || filters.platform == null ? undefined : filters.platform,
    createdFrom: filters.createdFrom ? dateInputToCreatedFrom(filters.createdFrom) : undefined,
    createdTo: filters.createdTo ? dateInputToCreatedTo(filters.createdTo) : undefined,
  };
}

export function dateInputToCreatedFrom(date: string): string {
  return new Date(`${date}T00:00:00`).toISOString();
}

export function dateInputToCreatedTo(date: string): string {
  return new Date(`${date}T23:59:59.999`).toISOString();
}

function serializeContactsQueryEntries(
  entries: Array<[string, string | number | undefined]>,
): string {
  return entries
    .filter(([, value]) => value !== undefined && value !== "")
    .map(([key, value]) => `${key}=${String(value)}`)
    .join("&");
}

/** Stable serialized key for list-fetch effect dependencies. */
export function contactsListQueryKey(query: ContactListQuery): string {
  return serializeContactsQueryEntries([
    ["page", query.page ?? 1],
    ["pageSize", query.pageSize ?? 25],
    ["sort", query.sort],
    ["search", query.search],
    ["groupId", query.groupId],
    ["hasEmail", query.hasEmail],
    ["hasPhone", query.hasPhone],
    ["platform", query.platform],
    ["createdFrom", query.createdFrom],
    ["createdTo", query.createdTo],
  ]);
}

/** Query identity for matching-selection, ignoring page/pageSize/sort. */
export function contactsMatchingQueryKey(query: ContactListQuery): string {
  return serializeContactsQueryEntries([
    ["search", query.search],
    ["groupId", query.groupId],
    ["hasEmail", query.hasEmail],
    ["hasPhone", query.hasPhone],
    ["platform", query.platform],
    ["createdFrom", query.createdFrom],
    ["createdTo", query.createdTo],
  ]);
}

export function isContactsListQueryConstrained(query: ContactListQuery): boolean {
  return (
    Boolean(query.search) ||
    query.groupId != null ||
    (query.hasEmail != null && query.hasEmail !== "ANY") ||
    (query.hasPhone != null && query.hasPhone !== "ANY") ||
    (query.platform != null && query.platform !== "ANY") ||
    Boolean(query.createdFrom) ||
    Boolean(query.createdTo)
  );
}

/** Avoid replacing query state when the server echoes the same pagination. */
export function mergeContactsListPagination(
  prev: ContactListQuery,
  response: Pick<ContactListQuery, "page" | "pageSize">,
): ContactListQuery {
  const page = response.page ?? prev.page;
  const pageSize = response.pageSize ?? prev.pageSize;
  if (prev.page === page && prev.pageSize === pageSize) {
    return prev;
  }
  return { ...prev, page, pageSize };
}

export function activeContactsFilterCount(filters: ContactsFilterState): number {
  let count = 0;
  if (filters.hasEmail && filters.hasEmail !== "ANY") count += 1;
  if (filters.hasPhone && filters.hasPhone !== "ANY") count += 1;
  if (filters.platform && filters.platform !== "ANY") count += 1;
  if (filters.createdFrom) count += 1;
  if (filters.createdTo) count += 1;
  return count;
}

export const EMPTY_CONTACTS_FILTERS: ContactsFilterState = {};

export function hasActiveContactsFilters(filters: ContactsFilterState): boolean {
  return activeContactsFilterCount(filters) > 0;
}
